import { unsafeHTML } from '@kit/vendor/lit-directives-unsafe-html.js';
import { marked } from '@kit/vendor/marked.js';
import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Blocks a freelancer until they have accepted the current version of every
 * required platform document.
 *
 * Each document gets its own affirmative accept action rather than one blanket
 * checkbox — separate assent is what makes each document independently
 * provable. The document bodies come from the API (embedded in the binary), so
 * the markdown rendered here is our own text, never user input.
 */
class AgreementGate extends BaseComponent {
    static properties = {
        _agreements: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _accepting: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .gate { max-width: 720px; margin: 2rem auto; padding: 0 1rem; }
        .gate-header h4 { margin: 0 0 0.25rem; font-size: 1.5rem; font-weight: 600; }
        .gate-intro { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        .doc {
            border: 1px solid #e5e5e5; border-radius: 8px; margin-bottom: 1.25rem; overflow: hidden;
        }
        .doc-header {
            display: flex; justify-content: space-between; align-items: center;
            padding: 0.75rem 1rem; background: #fafafa; border-bottom: 1px solid #eee;
        }
        .doc-header .title { font-weight: 600; }
        .doc-body {
            padding: 1rem 1.25rem; max-height: 22rem; overflow-y: auto; font-size: 0.9rem;
            line-height: 1.55;
        }
        .doc-body h1 { font-size: 1.25rem; margin-top: 0; }
        .doc-body h2 { font-size: 1.05rem; margin-top: 1.25rem; }
        .doc-footer { padding: 0.75rem 1rem; border-top: 1px solid #eee; text-align: right; }
        .accepted-badge { color: #198754; font-size: 0.85rem; font-weight: 600; }
    `;

    constructor() {
        super();
        this._agreements = [];
        this._loading = true;
        this._error = null;
        this._accepting = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        await this._load();
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            const data = await api.get('/api/agreements');
            this._agreements = data.agreements || [];
            if (data.all_accepted) {
                this.dispatchEvent(
                    new CustomEvent('agreements-accepted', { bubbles: true, composed: true }),
                );
            }
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _accept(agreement) {
        this._error = null;
        this._accepting = agreement.key;
        try {
            await api.post(`/api/agreements/${agreement.key}/accept`, {
                version: agreement.version,
            });
        } catch (err) {
            this._error = err.message;
            return;
        } finally {
            this._accepting = null;
        }
        // The acceptance is committed once the POST resolves. Refresh outside the
        // try so a failed reload reports its own error (_load sets _error itself)
        // instead of reading as a failed acceptance and inviting the user to
        // re-accept something that already took.
        await this._load();
    }

    render() {
        if (this._loading) {
            return html`
                <div class="gate text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                </div>
            `;
        }

        return html`
            <div class="gate">
                <div class="gate-header"><h4>Before you get started</h4></div>
                <p class="gate-intro">
                    Please review and accept the following. These cover your use of this platform
                    and how we handle your information — they do not change your services
                    agreement.
                </p>

                ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

                ${this._agreements.map(
                    (a) => html`
                    <div class="doc">
                        <div class="doc-header">
                            <span class="title">${a.title}</span>
                            ${a.accepted ? html`<span class="accepted-badge">Accepted</span>` : ''}
                        </div>
                        <div class="doc-body">${unsafeHTML(marked.parse(a.body || ''))}</div>
                        ${
                            a.accepted
                                ? ''
                                : html`
                            <div class="doc-footer">
                                <button
                                    class="btn btn-primary btn-sm"
                                    ?disabled="${this._accepting === a.key}"
                                    @click="${() => this._accept(a)}"
                                >
                                    ${this._accepting === a.key ? 'Recording…' : `I accept the ${a.title}`}
                                </button>
                            </div>
                        `
                        }
                    </div>
                `,
                )}
            </div>
        `;
    }
}

customElements.define('agreement-gate', AgreementGate);
